// src/utils/materialHelpers.js

/**
 * List of material codes that should be treated as infinite/water resources
 * These materials will never be deducted from stock and always appear as available
 */
export const WATER_MATERIALS = ['MP001', 'AGUA001', 'WATER001'];

/**
 * Checks if a material should be treated as an infinite resource (like water)
 * @param {Object} material - Material object from the unified collection
 * @returns {boolean} - True if material is water/infinite resource
 */
export const isWaterMaterial = (material) => {
    if (!material) return false;

    // Check by code
    if (material.code && WATER_MATERIALS.includes(material.code.toUpperCase())) {
        return true;
    }

    // Check by name variations
    const name = (material.commercialName || material.name || '').toUpperCase();
    const waterNames = ['ÁGUA', 'AGUA', 'WATER', 'AQUA'];
    if (waterNames.some(waterName => name.includes(waterName))) {
        return true;
    }

    // Check by INCI name
    const inci = (material.inciName || '').toUpperCase();
    if (waterNames.some(waterName => inci.includes(waterName))) {
        return true;
    }

    return false;
};

/**
 * Gets the appropriate unit for a material based on its type and categories
 * @param {Object} material - Material object
 * @param {Array} categories - List of all categories
 * @returns {string} - Unit abbreviation
 */
export const getMaterialUnit = (material, categories) => {
    if (!material) return 'Un';

    // Check if it's a packaging/serigraphy material
    const packagingCategories = ['EMBALAGEM', 'SERIGRAFIA'];
    const isPackaging = material.categoryIds?.some(catId => {
        const category = categories?.find(c => c.id === catId);
        return packagingCategories.includes(category?.name?.toUpperCase());
    });

    if (isPackaging) return 'Un';

    // Check gramatura unit for products
    if (material.gramaturaUnit) {
        if (material.gramaturaUnit === 'ml' || material.gramaturaUnit === 'L') return 'L';
        if (material.gramaturaUnit === 'g' || material.gramaturaUnit === 'Kg') return 'Kg';
    }

    // Defaults
    if (material.type === 'raw_material') return 'Kg';
    return 'Un';
};

/**
 * Formats a material for display in dropdowns and tables
 * @param {Object} material - Material object
 * @returns {string} - Formatted display string
 */
export const formatMaterialDisplay = (material) => {
    if (!material) return '';
    const name = material.commercialName || material.name || material.inciName || 'Sem nome';
    const code = material.code || material.id?.slice(-6) || '';
    return `${code ? `${code} - ` : ''}${name}`;
};